import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, Injector, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { Roles } from 'src/app/common/enumerations/enumerations';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { APLCategory } from 'src/app/components/apl/services/apl-category.model';
import { APLGroup } from 'src/app/components/apl/services/apl-group.model';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { User } from 'src/app/components/catalogs/user-catalog/services/user';
import { ProcedureCredited } from '../../models/procedure-credited.model';
import { ProcedureTrainingCredited } from '../../models/procedure-training-credited.model';
import { ProcedureCreditedService } from '../../services/procedure-credited.service';
import { CreditedDatePopupComponent } from '../credited-date-popup/credited-date-popup.component';
import { Procedure } from '../../models/procedure.model';
import { InfoDialogComponent } from 'src/app/controls/info-dialog/info-dialog.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatCheckboxChange, MatCheckboxClickAction } from '@angular/material/checkbox';


@Component({
  selector: 'procedure-trainings-table-component',
  templateUrl: './procedure-trainings-table.component.html',
  styleUrls: ['./procedure-trainings-table.component.scss']
})
export class ProcedureTrainingsTableComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
  // Inputs
  @Input() user?: User | null;
  @Input() trainings: any;
  @Input() supervised: User[] = [];
  @Input() label_procedure_number = '';
  @Input() label_procedure_title = '';
  @Input() label_training_complete_option = '';
  @Input() label_training_remove_option = '';
  @Input() admin = false;
  @Input() completeTrainingInfo: any;
  @Input() extraTrainingsPermission = false;
  @Input() filter: any;
  @Input() showAppendices?: boolean;
  @Input() selectMultiple = false;
  @Output() deleteProcedureTraining: EventEmitter<{ procedureId: number, userId: number; }> = new EventEmitter<{ procedureId: number, userId: number; }>();

  @Output() selected = new EventEmitter<boolean>();

  proceduresBeingCompleted: number[] = [];



  columns: string[] = ['select', 'procedureNumber', 'title', 'currentRevision', 'revisionCredited', 'obligationType', 'dateCredited', 'creditedBy', 'applicableRoles', 'actions'];

  aplCategories$!: Observable<APLCategory[]>;
  aplCategories!: APLCategory[];
  aplCategoriesSubs!: Subscription;
  aplCategoryRoles: Role[] = [];
  aplCategoriesFiltered!: APLCategory[];

  aplGroups!: APLGroup[];
  aplGroups$!: Observable<APLGroup[]>;
  aplGroupsSubs!: Subscription;
  aplGroupsFiltered!: APLGroup[];

  roles!: Role[];
  roles$!: Observable<Role[]>;
  rolesSubs!: Subscription;

  dataSource = new MatTableDataSource<any>();

  constructor(
    protected override injector: Injector,
    private procedureCreditedService: ProcedureCreditedService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.trainings) {
      this.trainings.filter = this.filter?.toLowerCase();
    }
    this.loadAPLCategories();
    this.loadRoles();
    this.setColumns();
  }

  ngOnInit(): void {
    this.setLabels();
  }

  setColumns() {
    if (this.selectMultiple) {
      this.columns = ['procedureNumber', 'title', 'currentRevision', 'revisionCredited', 'obligationType', 'dateCredited', 'creditedBy', 'applicableRoles', 'select'];
    } else {
      this.columns = ['procedureNumber', 'title', 'currentRevision', 'revisionCredited', 'obligationType', 'dateCredited', 'creditedBy', 'applicableRoles', 'actions'];
    }
    this.dataSource = new MatTableDataSource<any>(this.trainings?.filteredData ?? []);
  }

  override ngOnDestroy(): void {
    this.aplGroupsSubs?.unsubscribe();
    this.aplCategoriesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  loadAPLCategories() {
    this.aplCategories$ = this.store.select(state => state.APLCategory.APLCategories);
    this.aplCategoriesSubs = this.aplCategories$.subscribe(data => {
      if (data?.length && this.user?.id) {
        this.aplCategories = data;
        this.aplCategoriesFiltered = data.filter(x => x.aplCategoryManagers.map(m => m.userID).includes(this.user?.id ?? 0));
        this.loadAPLGroups();
      }
    });
  }

  loadAPLGroups() {
    this.aplGroups$ = this.store.select(state => state.APLGroup.all);
    this.aplGroupsSubs = this.aplGroups$.subscribe(data => {
      this.aplGroups = data;
      this.aplGroupsFiltered = data.filter(g => this.aplCategoriesFiltered.map(c => c.aplCategoryMasterID).includes(g.aplGroupMaster?.aplCategoryMasterID ?? 0) || g.aplGroupManagers?.map(m => m.userID).includes(this.user?.id) || g.aplProcedureTrainingCoordinators?.map(c => c.userID).includes(this.user?.id));
      this.aplGroupsFiltered.map(g => {
        const roles = g.aplGroupRoles?.filter(gr => gr.role).map(gr => gr.role as Role);
        if (roles)
          this.aplCategoryRoles.push(...roles);
      });
    });
  }

  loadRoles() {
    this.roles$ = this.store.select(state => state.Roles.data);
    this.rolesSubs = this.roles$.subscribe(data => {
      this.roles = data;
    });
  }

  canComplete(e: any): boolean {
    const val = this.completeTrainingInfo?.user?.id == this.user?.id && e.procedureCredited?.procedure?.version != e.procedure?.version && e.procedureCredited?.status != 0 && this.proceduresBeingCompleted.indexOf(e.procedure?.id) === -1 && !this.canCompleteAndApprove(e);
    e.canComplete = val;
    this.completeTrainingInfo.canComplete = this.trainings.data.some((t: any) => t.selected && t.canComplete);
    return val;
  }

  canApprove(e: any): boolean {
    const val = this.trainingCanBeApproved(e) && (this.utils.intersect(this.aplCategoryRoles.map(c => c.id), e.applicableRoles.map((a: any) => a.id)) || (this.admin || this.hasRoles([Roles.SA, Roles.PC])));
    e.canApprove = val;
    this.completeTrainingInfo.canApprove = this.trainings.data.some((t: any) => t.selected && t.canApprove);
    return val;
  }

  canCompleteAndApprove(e: any) {
    const val = (this.hasRoles([Roles.PC]) || (this.completeTrainingInfo?.user?.id == this.user?.id && e.obligationType < 3)) && e.procedureCredited?.procedure?.version != e.procedure?.version && e.procedureCredited?.status != 0 && this.proceduresBeingCompleted.indexOf(e.procedure?.id) === -1;
    e.canCompleteAndApprove = val;
    this.completeTrainingInfo.canCompleteAndApprove = this.trainings.data.some((t: any) => t.selected && t.canCompleteAndApprove);
    return val;
  }

  canUndo(e: any): boolean {
    return this.completeTrainingInfo?.user?.id == this.user?.id && e.procedureCredited?.status == 0;
  }

  canDelete(e: any): boolean {
    return (!e.procedureCredited && this.extraTrainingsPermission) || (this.completeTrainingInfo?.user?.id == this.user?.id && !e.procedureCredited && !e.applicableRoles);
  }

  canRemoveTrainingCredit(e: any): boolean {
    return e.procedure.version == e.procedureCredited?.procedure?.version && (e.procedureCredited?.status == 1 && (this.canRemoveOnNormalSection(e) || this.canRemoveOnExtraSection(e)));
  }

  canRemoveOnNormalSection(e: any): boolean {
    return e.applicableRoles != '' && (this.user?.userRole?.some(r => r.roleID === Roles.SA || r.role?.code === 'PC' || r.role?.code === 'PTC') || this.utils.intersect(this.aplCategoryRoles.map(c => c.id), e.applicableRoles.map((a: any) => a.id)));
  }

  canRemoveOnExtraSection(e: any): boolean {
    return this.hasRoles([Roles.SA, Roles.PC]) && !e.applicableRoles;
  }

  trainingCanBeApproved(e: any): boolean {
    return this.completeTrainingInfo?.user?.id != this.user?.id && e.procedureCredited?.status == 0;
  }

  hasActions(e: any): boolean {
    return this.canComplete(e) || this.canApprove(e) || this.canUndo(e) || this.canDelete(e) || this.canRemoveTrainingCredit(e) || this.canCompleteAndApprove(e);
  }

  formatRoles(roles: Role[]) {
    return roles.map(x => x.code).join(', ');
  }

  formatTooltip(roles: Role[]) {
    return roles.map(x => x.name).join(', ');
  }

  roleTooltip(applicableRoles: Role[]): string {
    if (applicableRoles.length) {
      return 'The following users can grant your training credit: <br><br>' + applicableRoles.map(aRole => {
        // Find the matching role in this.roles
        const match = this.roles?.find(r => r.id === aRole.id);

        // Extract manager names if present
        const groupManagerNames =
          match?.aplGroupRole?.aplGroup?.aplGroupManagers?.map(m => m.user?.name) || [];

        const coordinatorNames =
          match?.aplGroupRole?.aplGroup?.aplProcedureTrainingCoordinators?.map(m => m.user?.name) || [];

        const categoryManagerNames =
          match?.aplGroupRole?.aplGroup?.aplGroupMaster?.aplCategoryMaster?.aplCategory?.aplCategoryManagers?.map(m => m.user?.name) || [];

        // Join into "RoleName: Manager1, Manager2"
        return `
        <p>
          <b>${aRole.name}</b><br>
          <ul>
            <li><b>Group Manager(s):</b> ${groupManagerNames.join(", ") || "—"}</li>
            <li><b>Training Coordinator(s):</b> ${coordinatorNames.join(", ") || "—"}</li>
            <li><b>Category Manager(s):</b> ${categoryManagerNames.join(", ") || "—"}</li>
          </ul>
        </p>
      `;
      }).join("<br>");
    }
    else {
      return 'Extra Training Credit can only be granted by the Procedure Center Manager';
    }
  }

  infoPopup(roles: Role[]) {
    const dialog = this.dialog.open(InfoDialogComponent, {
      width: '45vw',
      data: {
        title: 'Procedure Training Workflow',
        subTitle: 'You have marked your training complete, the next step is to get credit for it.',
        message: this.roleTooltip(roles),
        footer: 'Note: Users are not permitted to approve their own training'
      }
    });
  }

  complete(procedure: Procedure) {
    return new Promise((resolve, reject) => {
      this.dialog.open(YesNoDialogComponent, {
        width: '45vw',
        data: {
          message: this.label_training_complete_option,
          changeButtonLabels: true,
          labelButtonCancel: 'Cancel',
          labelButtonAccept: 'Confirm'
        }
      }).afterClosed().subscribe(
        data => {
          if (data) {
            this.proceduresBeingCompleted.push(procedure.id);
            this.procedureCreditedService.complete(procedure.id).subscribe(
              procedureCredited => {
                resolve(procedureCredited);
              },
              error => {
                if (error.status === 404 || error.status === 409) {
                  this.alert.error(error.error);
                } else {
                  this.alert.defaultError();
                }
                console.error(error);
              }, () => {
                this.removeProcedureBeingCompleted(procedure.id);
              }
            );
          }
        }
      );
    });

  }

  removeProcedureBeingCompleted(procedureID: number) {
    const procedureCompletedIndex = this.proceduresBeingCompleted.indexOf(procedureID);
    if (procedureCompletedIndex > -1) {
      this.proceduresBeingCompleted.splice(procedureCompletedIndex, 1);
    }
  }

  approve(procedureCredited: ProcedureCredited) {
    this.dialog.open(CreditedDatePopupComponent, {
      width: '25vw',
      data: {
        procedure: procedureCredited?.procedure
      }
    }).afterClosed().subscribe(
      data => {
        if (data) {
          const procedureTrainingCredited = data as ProcedureTrainingCredited;
          if (procedureCredited.id)
            this.procedureCreditedService.approve(procedureCredited?.id, procedureTrainingCredited).subscribe(
              () => {
              },
              error => {
                if (error.status === 404 || error.status === 409) {
                  this.alert.error(error.error);
                } else {
                  this.alert.defaultError();
                }
                console.error(error);
              },
            );
        }
      }
    );
  }

  completeAndApprove(procedure: Procedure) {

    if (this.hasRoles([Roles.PC])) {
      this.dialog.open(CreditedDatePopupComponent, {
        width: '25vw',
        data: {
          procedure: procedure
        }
      }).afterClosed().subscribe(
        data => {
          if (data) {
            this.proceduresBeingCompleted.push(procedure.id);
            this.procedureCreditedService.complete(procedure.id, this.completeTrainingInfo?.user?.id).subscribe(
              procedureCredited => {
                if (procedureCredited.id) {
                  const procedureTrainingCredited = procedureCredited as ProcedureTrainingCredited;
                  procedureTrainingCredited.creditDate = data.creditDate;
                  this.removeProcedureBeingCompleted(procedure.id);
                  this.procedureCreditedService.approve(procedureCredited?.id, procedureTrainingCredited).subscribe(
                    () => {
                    },
                    error => {
                      if (error.status === 404 || error.status === 409) {
                        this.alert.error(error.error);
                      } else {
                        this.alert.defaultError();
                      }
                      console.error(error);
                    },
                  );
                }
              },
              error => {
                if (error.status === 404 || error.status === 409) {
                  this.alert.error(error.error);
                } else {
                  this.alert.defaultError();
                }
                console.error(error);
              }, () => {

              }
            );
          }
        });
    }
    else {
      this.dialog.open(YesNoDialogComponent, {
        width: '45vw',
        data: {
          message: this.label_training_complete_option,
          changeButtonLabels: true,
          labelButtonCancel: 'Cancel',
          labelButtonAccept: 'Confirm'
        }
      }).afterClosed().subscribe(
        data => {
          if (data) {
            this.proceduresBeingCompleted.push(procedure.id);
            this.procedureCreditedService.complete(procedure.id, this.completeTrainingInfo?.user?.id).subscribe(
              procedureCredited => {
                if (procedureCredited.id) {
                  const procedureTrainingCredited = procedureCredited as ProcedureTrainingCredited;
                  procedureTrainingCredited.creditDate = new Date();
                  this.removeProcedureBeingCompleted(procedure.id);
                  this.procedureCreditedService.approve(procedureCredited?.id, procedureTrainingCredited).subscribe(
                    () => {
                    },
                    error => {
                      if (error.status === 404 || error.status === 409) {
                        this.alert.error(error.error);
                      } else {
                        this.alert.defaultError();
                      }
                      console.error(error);
                    },
                  );
                }
              },
              error => {
                if (error.status === 404 || error.status === 409) {
                  this.alert.error(error.error);
                } else {
                  this.alert.defaultError();
                }
                console.error(error);
              }, () => {

              }
            );
          }
        });
    }
  }

  getInitials(e: any) {
    if (e.creditedBy) {
      return e.creditedBy?.initials;
    }
    else if (e.creditedDate &&
      e.creditedRev &&
      (e.procedureCredited?.status != 0 || e.procedure?.status != 1) &&
      !(
        e.procedure?.revision != e.procedureCredited?.procedure?.revision &&
        e.procedure?.status == 1
      )) {
      return 'ASCC';
    }

  }

  removeProcedureCredited(procedureCreditedID: number) {
    this.dialog
      .open(YesNoDialogComponent, {
        width: '45vw',
        data: {
          message: this.label_training_remove_option,
          changeButtonLabels: true,
          labelButtonCancel: 'Cancel',
          labelButtonAccept: 'Confirm',
        },
      }).afterClosed().subscribe((data) => {
        if (data) {
          this.procedureCreditedService
            .removeProcedureCredited(procedureCreditedID)
            .subscribe(
              () => {
              },
              (error) => {
                if (error.status === 404 || error.status === 409) {
                  this.alert.error(error.error);
                } else {
                  this.alert.defaultError();
                }
                console.error(error);
              }
            );
        }
      });
  }

  deleteTraining(procedureId: number) {
    this.deleteProcedureTraining.emit({ procedureId, userId: this.completeTrainingInfo?.user?.id });
  }

  setLabels() {
    this.label_procedure_number = this.getMessage('ALSPC_ProcedureTraining_ProcedureNumber')?.description;
    this.label_procedure_title = this.getMessage('ALSPC_ProcedureTraining_Title')?.description;
    this.label_training_complete_option = this.getMessage('Training_Complete_Option')?.description;
    this.label_training_remove_option = this.getMessage('Training_Remove_Option')?.description;
    if (!this.label_procedure_number) {
      setTimeout(() => {
        this.setLabels();
      }, 1000);
    }
  }

  toggleSelection(e: any, value: MatCheckboxChange) {
    this.trainings.data.find((t: any) => t.procedure.id === e.procedure.id).selected = value.checked;
    this.computeActions();
  }

  computeActions() {
    this.trainings.data.map((t: any) => {
      t.canComplete = this.canComplete(t);
      t.canApprove = this.canApprove(t);
      t.canCompleteAndApprove = this.canCompleteAndApprove(t);
    });

    const selectedLength = this.trainings.data.filter((t: any) => t.selected).length;
    this.selected.emit(selectedLength > 0);
  }

}
